import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
	LWApiPublicMarketCartOrderParamsRawI,
	LWApiPublicMarketCartDetailsResultRawI,
} from '@liquidweb-dev/club-sauce/public/market/cart/index.raw';
import type { LWApiPublicOrderListResultRaw } from '@liquidweb-dev/club-sauce/public/order/index.raw';
import type { ApiErrorI } from '@liquidweb-dev/club-sauce/types/apiError';
import api from 'modules/queries/api';
import { ORDER_LIST_QUERY_KEY } from 'modules/queries/order/useList';
import useSnackbarError from 'utility/hooks/useSnackbarError';
import { MARKET_CART_DETAILS_QUERY_KEY } from './useDetails';

const useCartOrder = () => {
	const snackbarError = useSnackbarError();
	const queryClient = useQueryClient();

	return useMutation<
		LWApiPublicMarketCartDetailsResultRawI,
		ApiErrorI,
		LWApiPublicMarketCartOrderParamsRawI
	>({
		mutationFn: (params) =>
			api.market.cart.apiRaw.order({
				alsowith: ['items', 'metadata'],
				...params,
			}),
		onSuccess: (data) => {
			// Pessimistically updates the market cart details cache with the ordered
			// cart.
			queryClient.setQueryData(
				[MARKET_CART_DETAILS_QUERY_KEY, { uuid: data.uuid }],
				data,
			);
			// Pessimistically updates the cached order lists with the order.
			queryClient.setQueriesData<LWApiPublicOrderListResultRaw>(
				[ORDER_LIST_QUERY_KEY],
				(orderList) =>
					orderList &&
					!orderList.items.some(
						({ market_cart_uuid: marketCartUuid }) =>
							marketCartUuid === data.uuid,
					)
						? {
								...orderList,
								items: [
									{
										market_cart_uuid: data.uuid,
										order_date: new Date().toISOString(),
										order_id: data.metadata?.order_id || '',
										status: 'new',
									},
									...orderList.items,
								],
							}
						: orderList,
			);
			queryClient.invalidateQueries({
				queryKey: [MARKET_CART_DETAILS_QUERY_KEY, { uuid: data.uuid }],
			});
			queryClient.invalidateQueries({ queryKey: [ORDER_LIST_QUERY_KEY] });
		},
		onError: snackbarError,
	});
};

export default useCartOrder;
